import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShoppingCart, BookOpen, Star, CheckCircle } from 'lucide-react';

const BookPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const keyTakeaways = [
    'How to build a customer-centric culture from the ground up',
    'Proven strategies for implementing servant leadership principles',
    'Real-world examples from successful businesses',
    'Practical frameworks for measuring customer satisfaction',
    'Techniques for empowering teams to deliver exceptional service',
    'Methods for aligning business goals with customer needs'
  ];

  return (
    <div data-testid="book-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="glass-card rounded-2xl p-12 shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_growth-guide-21/artifacts/g2t7zcvj_BOOK%20COVER%20BACK%202.jpg"
                  alt="The Customer First Business Book - Back Cover"
                  className="w-full rounded-lg shadow-xl"
                  data-testid="book-cover-image"
                />
              </div>
            </motion.div>

            <motion.div {...fadeInUp}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="book-title">
                The Customer First Business
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
                A comprehensive guide to transforming your organization through servant leadership 
                and customer-first principles. Learn how the world's most successful companies put 
                customers at the center of everything they do.
              </p>

              <div className="space-y-4">
                <a
                  href="https://www.amazon.com/Customer-First-Business-Blueprint-Innovation-ebook/dp/B0GJFNY1QD/ref=sr_1_1?dib=eyJ2IjoiMSJ9.6vYa2tjD0i-VCnfODAfs3XdC9GcgwuenI6RmdN0tu_y5HwCDIL8r0KPxpf2Ub1ePsZazmmVeaKQXDKVr2jyC9pYIwinSe899zDWs2j7U0rCWrWOiiXGDAHaQm0exx8ntHBZKc_KtpmBZYLjqNehrJljrDsgFXptdgQdrO48pypooG_BpGM0HpWv-DNfCLsvWn6cBLx51kJtjpt1X_iRjFgpL3MOG3r1GYKE35MD-CfE.JxVc7737meCfNNcZkrPkSQgc5yIn4yDwbYwvAixthEc&dib_tag=se&keywords=The+Customer+First+Business&qid=1769302525&sr=8-1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buy-ebook-button"
                  className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg w-full sm:w-auto"
                >
                  <ShoppingCart className="mr-2" size={20} />
                  Get The E-Book Now
                </a>
                <a
                  href="https://www.amazon.com/Customer-First-Business-Blueprint-Innovation/dp/B0GKNKFL31/ref=tmm_pap_swatch_0?_encoding=UTF8&dib_tag=se&dib=eyJ2IjoiMSJ9.6vYa2tjD0i-VCnfODAfs3XdC9GcgwuenI6RmdN0tu_y5HwCDIL8r0KPxpf2Ub1ePsZazmmVeaKQXDKVr2jyC9pYIwinSe899zDWs2j7U0rCWrWOiiXGDAHaQm0exx8ntHBZKc_KtpmBZYLjqNehrJljrDsgFXptdgQdrO48pypooG_BpGM0HpWv-DNfCLsvWn6cBLx51kJtjpt1X_iRjFgpL3MOG3r1GYKE35MD-CfE.JxVc7737meCfNNcZkrPkSQgc5yIn4yDwbYwvAixthEc&qid=1769302525&sr=8-1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buy-paperback-button"
                  className="inline-flex items-center justify-center border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full px-8 py-4 text-lg font-medium transition-all w-full sm:w-auto"
                >
                  <BookOpen className="mr-2" size={20} />
                  Get The Paperback Now
                </a>
                <a
                  href="https://www.audible.com/pd/The-Customer-First-Business-Audiobook/B0GS6QCK65?source_code=ASSGB149080119000H&share_location=pdp"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buy-audiobook-button"
                  className="inline-flex items-center justify-center border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full px-8 py-4 text-lg font-medium transition-all w-full sm:w-auto"
                >
                  <BookOpen className="mr-2" size={20} />
                  Get The Audio Book Now
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="section-padding bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" data-testid="takeaways-title">
              What You'll Learn
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Practical insights and strategies you can implement immediately.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {keyTakeaways.map((takeaway, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`takeaway-${index}`}
                className="flex items-start space-x-4 glass-card rounded-xl p-6"
              >
                <CheckCircle className="text-accent flex-shrink-0 mt-1" size={24} />
                <p className="text-lg">{takeaway}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Preview */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card rounded-2xl p-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center" data-testid="preview-title">
              Inside The Book
            </h2>
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold mb-4">Section 1: Serve Your Niche</h3>
                <p className="text-muted-foreground leading-relaxed">
                  This section helps you identify your ideal community and commit to a specific market where you can actually stand out.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4">Section 2: Solve Their Problems</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Here, you'll learn how to diagnose your audience's deepest pain points and build the high-value solutions they are actually eager to pay for.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4">Section 3: Scale Your Business</h3>
                <p className="text-muted-foreground leading-relaxed">
                  This section focuses on building the systems and reach necessary to transform a successful solution into a sustainable, high-impact enterprise.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-accent/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="cta-title">
              Ready to Transform Your Business?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Get your copy today and start your journey toward building a truly customer-first organization.
            </p>
            <a
              href="https://www.amazon.com/Customer-First-Business-Blueprint-Innovation-ebook/dp/B0GJFNY1QD/ref=sr_1_1?dib=eyJ2IjoiMSJ9.6vYa2tjD0i-VCnfODAfs3XdC9GcgwuenI6RmdN0tu_y5HwCDIL8r0KPxpf2Ub1ePsZazmmVeaKQXDKVr2jyC9pYIwinSe899zDWs2j7U0rCWrWOiiXGDAHaQm0exx8ntHBZKc_KtpmBZYLjqNehrJljrDsgFXptdgQdrO48pypooG_BpGM0HpWv-DNfCLsvWn6cBLx51kJtjpt1X_iRjFgpL3MOG3r1GYKE35MD-CfE.JxVc7737meCfNNcZkrPkSQgc5yIn4yDwbYwvAixthEc&dib_tag=se&keywords=The+Customer+First+Business&qid=1769302525&sr=8-1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-buy-button"
              className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg"
            >
              <ShoppingCart className="mr-2" size={20} />
              Get The Book Now
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BookPage;